import ImageLoader from "@/Components/Media/ImageLoader"
import { isDetectionService } from "@/services/assets/parsers"
import type { IMediaThumbProps, IWebCOOSAssetSummaryView, IWebCOOSElement } from "@/services/assets/types"
import { utils } from "@axdspub/axiom-ui-utilities"
import { formatInTimeZone } from "date-fns-tz"
import { CameraIcon, PlayCircle, VideoIcon } from "lucide-react"
import type { ReactElement } from "react"
import { Link } from "react-router"


export const DefaultDateDisplay = ({
    dateString,
    lengthString,
    isVideo,
    className,
    defaultClassName
}: {
    date: Date,
    dateString: string,
    lengthString?: string,
    isVideo: boolean,
    asset: IWebCOOSAssetSummaryView,
    element: IWebCOOSElement,
    defaultClassName?: string,
    className?: string
}): ReactElement => {
    return (
        <p className={utils.makeClassName({
            className,
            defaultClassName
        })}>
            {
                isVideo
                    ? <VideoIcon className='inline h-4 w-4 mr-1' />
                    : <CameraIcon className='inline h-4 w-4 mr-1' />
            }
            {dateString}
            {
                isVideo && lengthString
                    ? <> ({lengthString})</>
                    : null
            }
        </p>

    )
}


const MediaThumb = ({
    element,
    asset,
    serviceSlug,
    assetLabel,
    className,
    defaultClassName = 'relative w-full h-full',
    imageClassName,
    defaultImageClassName = 'object-cover w-full h-full',
    defaultDateDisplayClassName = 'absolute bottom-0 right-0 bg-primary shadow-md rounded-sm p-2 text-xs text-white m-2',
    dateDisplayClassName,
    defaultSize = 'medium',
    makeLink,
    DateDisplay = DefaultDateDisplay

}: IMediaThumbProps): ReactElement => {

    const timezoneForDisplay = asset.asset_timezone
        ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        ?? 'America/New_York';

    const dtFormat = (date: Date, fmt?: string) => formatInTimeZone(date, timezoneForDisplay, fmt || 'yyyy-MM-dd HH:mm:ss zzz');

    const dateString = dtFormat(new Date(element.data.extents.temporal.min));

    const isDetection = isDetectionService(serviceSlug)
    const detectionImageReferences = element?.data?.content?.map(c => c.annotated_image_reference)
    const detectionImageRoot = isDetection && detectionImageReferences?.[0] ? `https://app.webcoos.org/webcoos/api/v1/elements/${detectionImageReferences[0]}/thumbnail` : null

    const sm = detectionImageRoot 
        ? `${detectionImageRoot}?thumbnail_size=small`
        : element?.data?.properties?.thumbnails?.base?.rect_small
    const md = detectionImageRoot 
        ? `${detectionImageRoot}?thumbnail_size=med`
        : element?.data?.properties?.thumbnails?.base?.rect_medium
    const fl = detectionImageRoot 
        ? `${detectionImageRoot}?thumbnail_size=lqip`
        : element?.data?.properties?.url
    
    //const altFl = element?.data?.content?.[0]?.annotated_image_url

    const imageUrls = (defaultSize === 'full' ? [
        fl,
        //altFl,
        md,
        sm
    ] : defaultSize === 'small' ? [
        sm,
        md,
        fl,
        //altFl
    ] : [
        md,
        sm,
        fl,
        //altFl

    ]).concat(detectionImageReferences && detectionImageReferences.length > 1 ? detectionImageReferences?.slice(1).map(r => `https://app.webcoos.org/webcoos/api/v1/elements/${r}/thumbnail`) : [])

    const isVideo = element?.data?.properties?.url?.toLowerCase().endsWith('.mp4')
    const lengthString = isVideo && element.data.properties.duration
        ? (() => {
            const totalSeconds = Math.floor(element.data.properties.duration / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            return `${hours > 0 ? hours + 'h ' : ''}${minutes > 0 ? minutes + 'm ' : ''}${seconds}s`;
        })()
        : undefined


    const link = makeLink ? makeLink(element, asset) : undefined

    const image = imageUrls !== null
        ?
        <ImageLoader
            url={imageUrls}
            label={assetLabel ?? ''}
            className={utils.makeClassName({
                className: imageClassName,
                defaultClassName: defaultImageClassName
            })}
            loading='lazy'
            errorClassName="h-full w-full"
            Overlay={isVideo ? <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <PlayCircle className="h-12 w-12 text-white/50 group-hover:text-white drop-shadow-lg z-20" />
            </div> : undefined}
        />
        : <div className='bg-slate-200 p-10 h-full text-slate-400 text-center text-xs'>No thumbnail image available</div>

    const dateDisplay = <DateDisplay
        date={new Date(element.data.extents.temporal.min)}
        dateString={dateString}
        lengthString={lengthString}
        asset={asset}
        element={element}
        isVideo={isVideo}
        className={dateDisplayClassName}
        defaultClassName={defaultDateDisplayClassName}
    />
    const output = <>{image}{dateDisplay}</>


    return (
        <div className={
            utils.makeClassName({
                className,
                defaultClassName
            })
        }>
            {
                link !== undefined && link !== null
                    ? <Link to={link}>{output}</Link>
                    : output
            }

        </div>
    )
}

export default MediaThumb